import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  HttpStatusCode,
  isAxiosError,
} from 'axios';
import qs from 'qs';

import { authenticationSession } from '@/lib/authentication-session';
import { handleAuthError } from '@/lib/token-refresh';

import { API_URL, isUnauthenticatedRoute } from './api-constants';


function isUrlRelative(url: string) {
  return !url.startsWith('http') && !url.startsWith('https');
}

function request<TResponse>(
  url: string,
  config: AxiosRequestConfig = {},
): Promise<TResponse> {
  const resolvedUrl = !isUrlRelative(url) ? url : `${API_URL}${url}`;
  const isApWebsite = resolvedUrl.startsWith(API_URL);
  const unAuthenticated = isUnauthenticatedRoute(url);
  const token = authenticationSession.getToken();
  const requestConfig: AxiosRequestConfig = {
    url: resolvedUrl,
    ...config,
    withCredentials: isApWebsite,
    headers: {
      ...config.headers,
      Authorization:
        unAuthenticated || !isApWebsite || !token
          ? undefined
          : `Bearer ${token}`,
    },
  };
  return axios(requestConfig)
    .then((response) =>
    config.responseType === 'blob'
      ? response.data
      : (response.data as TResponse),
    )
    .catch((error) => {
      if (isAxiosError(error)) {
        return handleAuthError(error, requestConfig).then((response) =>
          config.responseType === 'blob'
            ? response.data
            : (response.data as TResponse),
        );
      }
      throw error;
    });
}

export type HttpError = AxiosError<unknown, AxiosResponse<unknown>>;

export const api = {
  isError(error: unknown): error is HttpError {
    return isAxiosError(error);
  },
  get: <TResponse>(url: string, query?: unknown, config?: AxiosRequestConfig) =>
    request<TResponse>(url, {
      params: query,
      paramsSerializer: (params) => {
        return qs.stringify(params, {
          arrayFormat: 'repeat',
        });
      },
      ...config,
    }),
    delete: <TResponse>(
      url: string,
      query?: Record<string, string>,
      body?: unknown,
    ) =>
    request<TResponse>(url, {
      method: 'DELETE',
      params: query,
      data: body,
      paramsSerializer: (params) => {
        return qs.stringify(params, {
          arrayFormat: 'repeat',
        });
      },
    }),
  post: <TResponse, TBody = unknown, TParams = unknown>(
    url: string,
    body?: TBody,
    params?: TParams,
    headers?: Record<string, string>,
  ) =>
    request<TResponse>(url, {
      method: 'POST',
      data: body,
      headers: { 'Content-Type': 'application/json', ...headers },
      params: params,
    }),

  patch: <TResponse, TBody = unknown, TParams = unknown>(
    url: string,
    body?: TBody,
    params?: TParams,
  ) =>
    request<TResponse>(url, {
      method: 'PATCH',
      data: body,
      headers: { 'Content-Type': 'application/json' },
      params: params,
    }),
  httpStatus: HttpStatusCode,
};
